/**
 * Component: VideoUploader
 * Purpose: Client-side video selection, compression, and upload UI.
 * Uses useVideoUpload hook for processing logic.
 */
import React, { useState, useRef, useEffect } from 'react';
import { X, Video, AlertCircle } from 'lucide-react';
import { 
  useVideoUpload, 
  PROCESSING_STATE, 
  convertGsUrlToHttps 
} from './useVideoUpload';

const VideoUploader = ({ 
  onVideoSelected, 
  onVideoUploaded, 
  uploadUrl, 
  error, 
  compressionTargetSizeMB = 0.9, 
  existingVideoUrl = null 
}) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [videoDuration, setVideoDuration] = useState(null);
  const fileInputRef = useRef(null);
  
  const {
    isProcessing,
    currentProcessingStep,
    progressPercent,
    processingError,
    videoUploaded,
    successMessage,
    handleUpload,
    cancelProcessing,
    setVideoUploaded,
    setProcessingError
  } = useVideoUpload({ compressionTargetSizeMB, onVideoUploaded });
  
  // Initialize with existing video if provided
  useEffect(() => {
    if (existingVideoUrl && !videoFile) {
      setFileName('Existing Video');
      setVideoUploaded(true);
    }
  }, [existingVideoUrl, videoFile, setVideoUploaded]);
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processVideoFile(file);
    }
  };

  const processVideoFile = (file) => {
    try {
      if (!file.type.match('video.*')) {
        setProcessingError('Please select a video file');
        return;
      }

      setFileName(file.name);
      setVideoFile(file);
      
      if (videoUrl && videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrl);
      }
      
      const url = URL.createObjectURL(file);
      setVideoUrl(url);
      
      // Get video duration
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        try {
          const duration = Math.round(video.duration);
          setVideoDuration(duration);
        } catch (err) {
          console.error('Error processing video metadata:', err);
        }
      };
      
      video.onerror = (e) => {
        console.error('Error loading video:', e);
        setProcessingError('Could not load video. The file may be corrupted or in an unsupported format.');
      };
      
      video.src = url;
      setProcessingError(null);
      
      if (onVideoSelected) {
        onVideoSelected(file);
      }
    } catch (err) {
      console.error('Error during video processing:', err);
      setProcessingError('Failed to process video file: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      processVideoFile(file);
    }
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleRemoveVideo = (e) => {
    if (e) e.stopPropagation();
    
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
    }
    
    setVideoFile(null);
    setVideoUrl(null);
    setFileName('');
    setVideoDuration(null);
    setProcessingError(null);
    setVideoUploaded(existingVideoUrl ? true : false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (onVideoSelected) {
      onVideoSelected(null);
    }
  };

  const onUploadClick = () => {
    handleUpload(videoFile, uploadUrl, videoDuration, fileName);
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (videoUrl && videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, []);

  return (
    <div className="space-y-3">
      <div
        onClick={!isProcessing ? (videoFile && videoUrl ? undefined : handleClick) : undefined}
        onDragOver={!isProcessing ? handleDragOver : undefined}
        onDragLeave={!isProcessing ? handleDragLeave : undefined}
        onDrop={!isProcessing ? handleDrop : undefined}
        className={`border-2 border-dashed rounded-lg p-5 text-center transition-colors ${
          isProcessing 
            ? 'cursor-default border-blue-300 dark:border-blue-700'
            : isDragging
              ? 'cursor-pointer border-primary-500 bg-primary-50 dark:bg-primary-900/20'
              : error || processingError
                ? 'cursor-pointer border-red-500 bg-red-50 dark:bg-red-900/20'
                : 'cursor-pointer border-gray-300 hover:border-primary-500 dark:border-gray-600 dark:hover:border-primary-500'
        }`}
      >
        {isProcessing && (
          <ProcessingIndicator 
            currentStep={currentProcessingStep}
            progressPercent={progressPercent}
            onCancel={cancelProcessing}
          />
        )}
        
        {!isProcessing && (videoUrl || (existingVideoUrl && !videoFile)) ? (
          <VideoPreview 
            videoUrl={videoUrl}
            existingVideoUrl={existingVideoUrl}
            fileName={fileName}
            videoFile={videoFile}
            videoUploaded={videoUploaded}
            onRemove={handleRemoveVideo}
            onUpload={onUploadClick}
          />
        ) : (
          !isProcessing && (
            <DropZoneContent />
          )
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          className="hidden"
          onChange={handleFileChange}
          disabled={isProcessing}
        />
      </div>
      
      {(error || processingError) && !isProcessing && (
        <div className="flex items-start rounded-md p-3.5 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0" />
          <span className="text-sm text-red-600 dark:text-red-400">
            {error || processingError}
          </span>
        </div>
      )}
    </div>
  );
};

// Processing Indicator Component
function ProcessingIndicator({ currentStep, progressPercent, onCancel }) {
  return (
    <div className="relative py-6">
      <div className="flex flex-col items-center justify-center space-y-4">
        <div className="text-center">
          <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
            {progressPercent}%
          </div>
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {currentStep === PROCESSING_STATE.COMPRESSING 
              ? 'Compressing video...' 
              : 'Uploading video...'}
          </h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {currentStep === PROCESSING_STATE.COMPRESSING
              ? 'This may take a few moments depending on video length'
              : 'Please keep this page open until upload completes'}
          </p>
        </div>
        
        <div className="w-full max-w-xs mx-auto">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              className="bg-blue-500 dark:bg-blue-600 h-2 rounded-full transition-all duration-300" 
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
        </div>
        
        <button
          type="button"
          onClick={onCancel}
          className="mt-2 inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <X className="h-3 w-3 mr-1" />
          Cancel
        </button>
      </div>
    </div>
  );
}

// Video Preview Component
function VideoPreview({ videoUrl, existingVideoUrl, fileName, videoFile, videoUploaded, onRemove, onUpload }) {
  return (
    <div className="relative">
      <div className="absolute top-0 right-0 p-1">
        <button
          onClick={onRemove}
          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md transition-colors"
          title={videoFile ? "Remove video" : "Remove and replace video"}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      
      <video
        src={videoUrl || convertGsUrlToHttps(existingVideoUrl)}
        controls
        preload="metadata"
        className="max-h-64 mx-auto rounded"
        poster={videoFile?.type === 'image/gif' ? (videoUrl || convertGsUrlToHttps(existingVideoUrl)) : undefined}
        onError={(e) => {
          console.warn('Video playback error:', e);
        }}
        onLoadStart={() => {
          console.log('Video started loading');
        }}
      >
        Your browser does not support the video tag.
      </video>
      
      {fileName && (
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 text-center">
          {fileName}
        </p>
      )}
      
      {!videoUploaded && videoFile && (
        <button
          type="button"
          onClick={onUpload}
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Upload Video
        </button>
      )}
      
      {videoUploaded && (
        <div className="mt-3 rounded-md bg-green-50 dark:bg-green-900/20 p-3 border border-green-100 dark:border-green-800">
          <p className="text-sm text-green-700 dark:text-green-300 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {videoFile ? 'Video uploaded successfully!' : 'Using existing video'}
          </p>
        </div>
      )}
    </div>
  );
}

// Drop Zone Content Component
function DropZoneContent() {
  return (
    <div className="py-8">
      <Video
        className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
        strokeWidth={1}
      />
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        Click to upload or drag and drop
      </p>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
        MP4, WebM, or MOV up to 500MB
      </p>
    </div>
  );
}

export default VideoUploader;
